import { createContext, useContext, useMemo, ReactNode } from "react";
import { useFirebaseAuth } from "../hooks/use-firebase-auth";
import { useFirebaseCart } from "../hooks/use-firebase-cart";

export type CartItem = {
  id: number | string;
  cartEntryId?: number | string;
  title: string;
  price: string | number;
  type: string;
  quantity: number;
  imageUrl?: string;
  details?: any;
};

type CartContextType = {
  items: CartItem[];
  addItem: (item: Omit<CartItem, "id"> & { id?: number | string }) => Promise<void> | void;
  removeItem: (id: number | string) => void;
  /** Remove several items in one write. */
  removeItems: (ids: (number | string)[]) => Promise<void>;
  updateQuantity: (id: number | string, quantity: number) => void;
  clearCart: () => void;
  itemCount: number;
  total: number;
  isLoading: boolean;
  refetchCart: () => void;
};

const CartContext = createContext<CartContextType | null>(null);

export function CartProvider({ children }: { children: ReactNode }) {
  const { user, isLoading: isAuthLoading } = useFirebaseAuth();
  const firebaseCart = useFirebaseCart();
  const isAuthenticated = !!user;

  // Destructure the referentially-stable pieces of the cart hook so the
  // memoized context value only changes when one of them actually changes.
  // useFirebaseCart() returns a fresh object literal every render, so memoizing
  // on `firebaseCart` directly would be a no-op — every useCart() consumer
  // (navbar, checkout, curriculum, etc.) would re-render on any provider render.
  const {
    items: fbItems,
    itemCount: fbItemCount,
    total: fbTotal,
    isLoading: fbIsLoading,
    addItem: fbAddItem,
    removeItem: fbRemoveItem,
    removeItems: fbRemoveItems,
    updateQuantity: fbUpdateQuantity,
    clearCart: fbClearCart,
    refetchCart: fbRefetchCart,
  } = firebaseCart;

  const contextValue = useMemo<CartContextType>(() => {
    const resolveCartEntryId = (id: number | string) => {
      const normalizedId = id.toString();
      const matchingItem = fbItems.find(
        (item) => item.id === normalizedId || item.itemId === normalizedId
      );
      return matchingItem?.id || normalizedId;
    };

    return {
      // Always use Firebase cart - requires authentication
      items: isAuthenticated ? fbItems.map(item => ({
        id: item.itemId || item.id,
        cartEntryId: item.id,
        title: item.title,
        price: item.price,
        type: item.itemType,
        quantity: item.quantity,
        imageUrl: item.imageUrl,
        details: item.details,
      })) : [],
      addItem: (item) => {
        if (!isAuthenticated) {
          console.warn("Cart operation requires authentication - redirecting to auth");
          window.location.href = '/auth?redirect=' + encodeURIComponent(window.location.pathname);
          return;
        }
        // Returned, not fire-and-forget: callers that navigate straight after
        // adding have to be able to await the write.
        return fbAddItem({
          id: item.id?.toString(),
          itemId: item.id?.toString() ?? '',
          itemType: item.type,
          title: item.title,
          price: typeof item.price === 'string' ? parseFloat(item.price) : item.price,
          quantity: item.quantity,
          imageUrl: item.imageUrl,
          details: item.details,
        });
      },
      removeItem: (id) => {
        if (!isAuthenticated) return;
        fbRemoveItem(resolveCartEntryId(id));
      },
      removeItems: async (ids) => {
        if (!isAuthenticated) return;
        await fbRemoveItems(ids.map(resolveCartEntryId));
      },
      updateQuantity: (id, quantity) => {
        if (!isAuthenticated) return;
        fbUpdateQuantity(resolveCartEntryId(id), quantity);
      },
      clearCart: () => {
        if (!isAuthenticated) return;
        fbClearCart();
      },
      itemCount: isAuthenticated ? fbItemCount : 0,
      total: isAuthenticated ? fbTotal : 0,
      isLoading: isAuthLoading || (isAuthenticated ? fbIsLoading : false),
      refetchCart: isAuthenticated ? fbRefetchCart : () => {},
    };
  }, [
    isAuthenticated,
    isAuthLoading,
    fbItems,
    fbItemCount,
    fbTotal,
    fbIsLoading,
    fbAddItem,
    fbRemoveItem,
    fbRemoveItems,
    fbUpdateQuantity,
    fbClearCart,
    fbRefetchCart,
  ]);

  return (
    <CartContext.Provider value={contextValue}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
}
